'use client';

import React, { createContext, useEffect, useState } from 'react';
import { Channel } from 'shared/types/channel';
import { useChannel } from 'frontastic';

export type UseChannelReturn = {
  channelsLoading: boolean;
  channels: Array<Channel>;
  selectedChannel?: Channel;
  setSelectedChannel: (channel: Channel | undefined) => void;
  isChannelFinderOpen: boolean;
  setIsChannelFinderOpen: (isChannelFinderOpen: boolean) => void;
  channelFinderDetailChannel?: Channel;
  setChannelFinderDetailChannel: (channelFinderDetailChannel: Channel | undefined) => void;
};

const ChannelContext = createContext({} as UseChannelReturn);

const ChannelProvider = ({ children }: React.PropsWithChildren) => {
  const [isChannelFinderOpen, setIsChannelFinderOpen] = useState(false);
  const [channelFinderDetailChannel, setChannelFinderDetailChannel] = useState<Channel | undefined>(undefined);
  const [selectedChannel, setSelectedChannel] = useState<Channel | undefined>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('selectedChannel');
      return saved !== null && saved !== undefined ? JSON.parse(saved) : undefined;
    } else {
      return undefined;
    }
  });

  useEffect(() => {
    if (selectedChannel === undefined) {
      localStorage.removeItem('selectedChannel');
    } else {
      localStorage.setItem('selectedChannel', JSON.stringify(selectedChannel));
    }
  }, [selectedChannel]);

  const { channels, isLoading } = useChannel();

  const channelReturn = {
    channels,
    channelsLoading: isLoading,
    selectedChannel,
    setSelectedChannel,
    isChannelFinderOpen,
    setIsChannelFinderOpen,
    channelFinderDetailChannel,
    setChannelFinderDetailChannel,
  };

  return <ChannelContext.Provider value={channelReturn}>{children}</ChannelContext.Provider>;
};

export const useChannelContext = () => {
  const context = React.useContext(ChannelContext);
  if (context === undefined) {
    throw new Error('useChannelContext must be used within a ChannelContextProvider');
  }
  return context;
};

export { ChannelContext, ChannelProvider };
